import axios from "axios";

// Ten endpoints, two scopes. patient_tier_config/patient_tier carry no branch
// column (a tier belongs to the whole clinic, never a single branch), so
// config/list/create/update/delete/report all authorize via authByClinicURL
// and their URLs carry no {branchUrl} — same reasoning as voucher.js's
// requireClinic. Per-patient endpoints (getPatientTier/recompute/override)
// ARE branch-scoped: a patient row belongs to a branch, and counter staff
// (branch roles) must reach them. Mixing the two up produces a 404 that
// looks like a missing route, not an auth error — see requireBranch below.
function requireClinic(clinicUrl, actionName) {
  if (!clinicUrl || clinicUrl === "undefined" || clinicUrl === "null" || clinicUrl.length > 50) {
    const error = new Error(`Invalid clinic parameter for ${actionName}: clinicUrl=${clinicUrl}`);
    error.code = "INVALID_PARAMS";
    console.error(`[Route Validation] ${error.message}`);
    throw error;
  }
}

function requireBranch(branchUrl, actionName) {
  if (!branchUrl || branchUrl === "undefined" || branchUrl === "null" || branchUrl.length > 50) {
    const error = new Error(`Invalid branch parameter for ${actionName}: branchUrl=${branchUrl}`);
    error.code = "INVALID_PARAMS";
    console.error(`[Route Validation] ${error.message}`);
    throw error;
  }
}

// The BE returns thresholdAmount/qualifiedAmount as STRINGS from some
// endpoints and NUMBERS from others — a known, deliberately deferred BE
// inconsistency (Sequelize DECIMAL columns serialize as strings when a raw
// model instance goes out over res.send, which listTiers/createTier/
// updateTier/getPatientTier/overridePatient all do; getTierReport and
// recomputePatient pre-coerce with Number() server-side before responding).
// Coerced once here — both on what gets committed AND on what the action
// resolves with, since some callers will read state via a getter and others
// will use the dispatch()'d value directly — so no component downstream
// ever has to special-case a string.
function coerceTierMoney(tier) {
  if (!tier) return tier;
  return { ...tier, thresholdAmount: Number(tier.thresholdAmount) };
}

function coerceStateMoney(state) {
  if (!state) return state;
  return { ...state, qualifiedAmount: Number(state.qualifiedAmount) };
}

function coerceEventMoney(event) {
  if (!event) return event;
  return { ...event, qualifiedAmount: Number(event.qualifiedAmount) };
}

function coerceReportRow(row) {
  if (!row || row.qualifiedAmount === undefined) return row;
  return { ...row, qualifiedAmount: Number(row.qualifiedAmount) };
}

const REPORT_TYPES = ["distribution", "roster", "tierUp"];

const moduleTier = {
  namespaced: true,
  state: {
    config: null,
    tierList: [],
    patientTier: null,
    report: [],
  },
  mutations: {
    setConfig(state, data) {
      state.config = data || null;
    },
    setTierList(state, data) {
      state.tierList = Array.isArray(data) ? data : [];
    },
    setPatientTier(state, data) {
      state.patientTier = data || null;
    },
    setReport(state, data) {
      state.report = Array.isArray(data) ? data : [];
    },
  },
  getters: {
    getConfig: state => state.config,
    getTierList: state => state.tierList,
    getPatientTier: state => state.patientTier,
    getReport: state => state.report,
  },
  actions: {
    // --- Clinic-scoped: authByClinicURL, no {branchUrl} in the URL. ---

    async getConfig({ commit }, payload) {
      requireClinic(payload.clinicUrl, "getConfig");
      return axios
        .get(`/api/patientTierConfig/${payload.clinicUrl}`)
        .then(res => {
          commit("setConfig", res.data);
          return res.data;
        });
    },

    async updateConfig({ commit }, payload) {
      requireClinic(payload.clinicUrl, "updateConfig");
      return axios
        .put(`/api/patientTierConfig/${payload.clinicUrl}`, payload.data)
        .then(res => {
          commit("setConfig", res.data);
          return res.data;
        });
    },

    async listTiers({ commit }, payload) {
      requireClinic(payload.clinicUrl, "listTiers");
      const query = payload.includeInactive ? "?includeInactive=true" : "";
      return axios
        .get(`/api/patientTier/${payload.clinicUrl}${query}`)
        .then(res => {
          const tiers = Array.isArray(res.data) ? res.data.map(coerceTierMoney) : [];
          commit("setTierList", tiers);
          return tiers;
        });
    },

    async createTier(_, payload) {
      requireClinic(payload.clinicUrl, "createTier");
      return axios
        .post(`/api/patientTier/${payload.clinicUrl}`, payload.data)
        .then(res => coerceTierMoney(res.data));
    },

    async updateTier(_, payload) {
      requireClinic(payload.clinicUrl, "updateTier");
      return axios
        .put(`/api/patientTier/${payload.clinicUrl}/${payload.tierId}`, payload.data)
        .then(res => coerceTierMoney(res.data));
    },

    async deleteTier(_, payload) {
      requireClinic(payload.clinicUrl, "deleteTier");
      return axios
        .delete(`/api/patientTier/${payload.clinicUrl}/${payload.tierId}`)
        .then(res => res.data);
    },

    // getTierReport is clinic-scoped like the four above it — see the header
    // comment on getTierReport.js: patient_tier_state/patient_tier_event carry
    // a branch_id column, but the REPORT itself is clinic-wide with branch as
    // an optional narrowing filter, not the authorization boundary.
    async getTierReport({ commit }, payload) {
      requireClinic(payload.clinicUrl, "getTierReport");
      if (!REPORT_TYPES.includes(payload.type)) {
        throw new Error(
          `getTierReport requires type to be one of ${REPORT_TYPES.join("|")}, got: ${payload.type}`
        );
      }
      const params = new URLSearchParams({ type: payload.type });
      if (payload.tierId != null) params.set("tierId", payload.tierId);
      if (payload.branchId != null) params.set("branchId", payload.branchId);
      if (payload.limit != null) params.set("limit", payload.limit);
      if (payload.offset != null) params.set("offset", payload.offset);
      if (payload.startDt) params.set("startDt", payload.startDt);
      if (payload.endDt) params.set("endDt", payload.endDt);

      return axios
        .get(`/api/patientTierReport/${payload.clinicUrl}?${params.toString()}`)
        .then(res => {
          const report = Array.isArray(res.data?.report) ? res.data.report.map(coerceReportRow) : [];
          commit("setReport", report);
          return report;
        });
    },

    // --- Branch-scoped: authByClinicURLBranchURL, {branchUrl} is required. ---

    async getPatientTier({ commit }, payload) {
      requireClinic(payload.clinicUrl, "getPatientTier");
      requireBranch(payload.branchUrl, "getPatientTier");
      return axios
        .get(`/api/patientTier/${payload.clinicUrl}/${payload.branchUrl}/patient/${payload.patientId}`)
        .then(res => {
          const data = {
            state: coerceStateMoney(res.data?.state),
            tier: coerceTierMoney(res.data?.tier),
            events: Array.isArray(res.data?.events) ? res.data.events.map(coerceEventMoney) : [],
          };
          commit("setPatientTier", data);
          return data;
        });
    },

    // Branch-scoped summary read (GET .../patient/{patientId}/summary,
    // authByClinicURLBranchURL + tier.view) — the endpoint PatientTierPanel.vue
    // uses for its whole summary block. It exists specifically so a pure
    // branch member (tier.view spans branch roles 1-5) never has to hit
    // listTiers, which is CLINIC-scoped (authByClinicURL) and 403s for
    // exactly that population — see PatientTierPanel.vue's header comment
    // on C1. Unlike getPatientTier this does not commit to shared state;
    // PatientTierPanel resolves it directly for the same reason documented
    // on getPatientTier/recomputePatient above.
    async getPatientTierSummary(_, payload) {
      requireClinic(payload.clinicUrl, "getPatientTierSummary");
      requireBranch(payload.branchUrl, "getPatientTierSummary");
      return axios
        .get(`/api/patientTier/${payload.clinicUrl}/${payload.branchUrl}/patient/${payload.patientId}/summary`)
        .then(res => ({
          tier: coerceTierMoney(res.data?.tier),
          qualifiedAmount: Number(res.data?.qualifiedAmount) || 0,
          amountToNextTier: res.data?.amountToNextTier != null ? Number(res.data.amountToNextTier) : null,
          vouchers: Array.isArray(res.data?.vouchers) ? res.data.vouchers : [],
        }));
    },

    async recomputePatient(_, payload) {
      requireClinic(payload.clinicUrl, "recomputePatient");
      requireBranch(payload.branchUrl, "recomputePatient");
      return axios
        .post(
          `/api/patientTier/${payload.clinicUrl}/${payload.branchUrl}/patient/${payload.patientId}/recompute`
        )
        .then(res => ({ ...res.data, qualifiedAmount: Number(res.data?.qualifiedAmount) }));
    },

    // PatientTierOverrideInput is additionalProperties:false and marks tierId
    // REQUIRED (though nullable) — overridePatient.js branches on
    // `body.tierId == null`, which is also true when the key is simply
    // omitted, so posting only { note } would silently be read by the BE as
    // "clear the pin" if the schema allowed it; instead the BE 400s because
    // the key is missing. `tierId: null` is the documented way to clear an
    // override — passing `payload` without a `tierId` key at all (as opposed
    // to `payload.tierId === null`) is a caller bug, not a valid "leave it
    // alone" request (there is no partial-update form of this endpoint), so
    // it throws client-side instead of sending a request the BE will 400.
    async overridePatient(_, payload) {
      requireClinic(payload.clinicUrl, "overridePatient");
      requireBranch(payload.branchUrl, "overridePatient");
      if (!Object.prototype.hasOwnProperty.call(payload, "tierId")) {
        throw new Error(
          "overridePatient requires tierId explicitly in the payload (pass null to clear the override)"
        );
      }
      const body = { tierId: payload.tierId };
      if (payload.note !== undefined) body.note = payload.note;

      return axios
        .post(
          `/api/patientTier/${payload.clinicUrl}/${payload.branchUrl}/patient/${payload.patientId}/override`,
          body
        )
        .then(res => ({
          state: coerceStateMoney(res.data?.state),
          tier: coerceTierMoney(res.data?.tier),
        }));
    },
  },
};

export default moduleTier;
